import { gql } from '@apollo/client';
import { MeetingPlaceLinkToAnyPageFragment } from './meetingPlaceLinkToAnyPage';

// Presentational fields for a single main banner. Shared by the banner itself
// and by its Ninetailed experience variants — which are also BlockMainBanner
// entries via Ninetailed's EntryReplacement model — so a personalized variant
// renders identically to a baseline banner.
export const MainBannerBlockBaseFragment = gql`
  ${MeetingPlaceLinkToAnyPageFragment}
  fragment MainBannerBlockBase on BlockMainBanner {
    blocktype: __typename
    sys {
      id
    }
    text
    title
    overlay
    mediaField {
      jsonBynderAsset
      altText
    }
    readMoreButtonTitle
    link {
      ...MeetingPlaceLinkToAnyPage
    }
    externalLink
  }
`;

// Banner selection used by the page-content queries (home, china, etc.).
// IMPORTANT: this intentionally contains ONLY the presentational base fields and
// NO Ninetailed experiences. Folding experiences in here previously multiplied
// the (already large) home query by experiences × variants per banner and blew
// past Contentful's GraphQL query limits (8kb size / 11000-entity complexity),
// causing `getHomePageData` to 400. Personalization is now fetched by a separate,
// minimal query (`HOME_MAIN_BANNER_EXPERIENCES_QUERY` / `MainBannerExperiences`)
// and merged into the home result by banner `sys.id`.
export const MainBannerBlockFragment = gql`
  ${MainBannerBlockBaseFragment}
  fragment MainBannerBlock on BlockMainBanner {
    ...MainBannerBlockBase
  }
`;

// Ninetailed experiences for a single main banner, fetched on its own so the
// heavy page-content query stays within Contentful's limits. Carries the
// banner's `sys.id` (the merge key back onto the home result) plus each
// experience's id, config, audience and variant banners — exactly the fields
// `mapExperiences` consumes. Limits are deliberately small: this feature is a
// single login-state (`isMember`) EntryReplacement per banner (1:1 slide swap),
// so a large fan-out would only inflate query complexity with no functional gain.
export const MainBannerExperiencesFragment = gql`
  ${MainBannerBlockBaseFragment}
  fragment MainBannerExperiences on BlockMainBanner {
    sys {
      id
    }
    ntExperiencesCollection(limit: 2) {
      items {
        ntExperienceId
        ntName
        ntType
        ntConfig
        ntAudience {
          ntAudienceId
          ntName
        }
        ntVariantsCollection(limit: 3) {
          items {
            ... on BlockMainBanner {
              ...MainBannerBlockBase
            }
          }
        }
      }
    }
  }
`;
